import * as Sentry from '@sentry/react';
import { Component, ErrorInfo, FunctionComponent, default as React, useEffect } from 'react';
import { StoreType } from '@tock/shared/utils/connect';
import { errorPageContent } from '@tock/shared/utils/errorContent';
import { useAppSettings } from 'selectors/settingsSelector';
import { useSelector } from 'react-redux';

export const GraphqlErrorBoundary: FunctionComponent = ({ children }) => {
  const graphqlError = useSelector((state: StoreType) => state.app.graphqlError);

  useEffect(() => {
    if (window?.LOGGER && graphqlError) {
      window.LOGGER?.error(graphqlError.message, graphqlError);
    }
  }, [graphqlError]);

  if (graphqlError) {
    return <ErrorBoundaryContent />;
  }
  return <>{children}</>;
};

const ErrorBoundaryContent: FunctionComponent<{}> = () => {
  return (
    <div
      dangerouslySetInnerHTML={{
        __html: errorPageContent`Sorry, Tock is temporarily unavailable and we are working hard to restore services. If you are trying to make a reservation or place an order, please contact the business directly. We apologize for the inconvenience and appreciate your patience.
  `,
      }}
    ></div>
  );
};

const ErrorBoundary: React.FC = ({ children }) => {
  const settings = useAppSettings();
  if (settings.DATADOG_ENV === 'staging') {
    return (
      <Sentry.ErrorBoundary fallback={<ErrorBoundaryContent />}>{children}</Sentry.ErrorBoundary>
    );
  } else {
    return <LegacyErrorBoundary>{children}</LegacyErrorBoundary>;
  }
};

export default ErrorBoundary;
class LegacyErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError(error: unknown) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    window.LOGGER?.error(error.message, error);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return <ErrorBoundaryContent />;
    }

    return this.props.children;
  }
}
